import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    CircularProgress,
    Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetchWithAuth } from "../../utils/api";

const AdminUpdateUserscript = () => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [statusLoading, setStatusLoading] = useState(true);
    const [triggerLoading, setTriggerLoading] = useState(false);
    const [error, setError] = useState("");
    const [latestRun, setLatestRun] = useState(null);
    const [lastTriggeredByDiscord, setLastTriggeredByDiscord] = useState("");

    const loadStatus = async () => {
        setStatusLoading(true);
        setError("");

        try {
            const response = await fetchWithAuth("/admin/userscript-refresh-status");
            setLatestRun(response.latestRun ?? null);
        } catch (err) {
            console.error("Failed to load userscript refresh status:", err);
            setError("Failed to load workflow status.");
        } finally {
            setStatusLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleConfirm = async () => {
        setTriggerLoading(true);
        setError("");

        try {
            const response = await fetchWithAuth("/admin/trigger-userscript-refresh", {
                method: "POST",
                body: {},
            });

            setSnackbarMessage(response.message || "Userscript refresh triggered successfully.");
            if (response.triggeredBy?.discordUsername) {
                setLastTriggeredByDiscord(response.triggeredBy.discordUsername);
            }
            setSnackbarOpen(true);
            setOpen(false);
            await loadStatus();
        } catch (err) {
            console.error("Failed to trigger userscript refresh:", err);
            setError("Failed to trigger the userscript refresh workflow.");
        } finally {
            setTriggerLoading(false);
        }
    };

    const lastUpdated = latestRun?.updatedAt
        ? new Date(latestRun.updatedAt).toLocaleString()
        : "N/A";

    const latestRunLabel = latestRun
        ? `${latestRun.status}${latestRun.conclusion ? ` (${latestRun.conclusion})` : ""}`
        : "No runs yet";
    const latestRunName = latestRun?.runName || "N/A";

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography
                variant="h5"
                sx={{ color: theme.palette.accent.main, mb: 2 }}
            >
                Update Userscript
            </Typography>

            {statusLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading workflow status...</Typography>
                </Box>
            ) : (
                <Box sx={{ mb: 3 }}>
                    <Typography sx={{ mb: 1 }}>
                        Last updated: {lastUpdated}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                        Latest run: {latestRunLabel}
                    </Typography>
                    <Typography sx={{ mb: latestRun?.htmlUrl ? 1 : 0 }}>
                        Run name: {latestRunName}
                    </Typography>
                    {lastTriggeredByDiscord && (
                        <Typography sx={{ mb: latestRun?.htmlUrl ? 1 : 0 }}>
                            Last manual trigger by Discord admin: {lastTriggeredByDiscord}
                        </Typography>
                    )}
                    {latestRun?.htmlUrl && (
                        <Link href={latestRun.htmlUrl} target="_blank" rel="noreferrer">
                            View workflow run
                        </Link>
                    )}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Button variant="contained" onClick={handleOpen} disabled={triggerLoading}>
                Update
            </Button>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to trigger the GitHub workflow to refresh the userscript values JSON?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={triggerLoading}>No</Button>
                    <Button variant="contained" onClick={handleConfirm} disabled={triggerLoading}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
            />
        </Box>
    );
};

export default AdminUpdateUserscript;
